export type PlainTextClipboardRuntime = {
	writeText?: (text: string) => Promise<void>;
	fallbackCopy: (text: string) => boolean | Promise<boolean>;
	reportError?: (error: unknown) => void;
};

const copyTextWithSelection = (text: string) => {
	if (typeof document === 'undefined' || typeof window === 'undefined') return false;

	const span = document.createElement('span');
	span.textContent = text;
	span.style.whiteSpace = 'pre';
	span.style.position = 'fixed';
	span.style.top = '0';
	span.style.left = '0';
	span.style.opacity = '0';
	document.body.appendChild(span);

	const range = document.createRange();
	range.selectNodeContents(span);
	const selection = window.getSelection();
	selection?.removeAllRanges();
	selection?.addRange(range);

	try {
		return document.execCommand('copy');
	} catch {
		return false;
	} finally {
		selection?.removeAllRanges();
		span.remove();
	}
};

const createBrowserClipboardRuntime = (): PlainTextClipboardRuntime => ({
	writeText:
		typeof navigator !== 'undefined' && navigator.clipboard?.writeText
			? navigator.clipboard.writeText.bind(navigator.clipboard)
			: undefined,
	fallbackCopy: copyTextWithSelection,
	reportError: (error) => console.error('Could not copy text to clipboard:', error)
});

export const writePlainTextToClipboard = async (
	text: string,
	runtime: PlainTextClipboardRuntime = createBrowserClipboardRuntime()
) => {
	if (runtime.writeText) {
		try {
			await runtime.writeText(text);
			return true;
		} catch (error) {
			runtime.reportError?.(error);
		}
	}

	try {
		return Boolean(await runtime.fallbackCopy(text));
	} catch (error) {
		runtime.reportError?.(error);
		return false;
	}
};
